import { Colors, Dropdown, DropdownListItem, useIsMobile, useToasts } from "@brg/ui";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { Wrapper } from "./style";
import {ReactComponent as ShevronSvg } from "./Arrows.svg";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { AppTester } from "supporting";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import { useDispatch, useSelector } from "react-redux";
import { useProfile } from "@brg/auth";
import { useRouteMatch } from "react-router-dom";
import { LinkTo } from "shared/constants/linkTo";
import { StyledDropdownListItem } from "../tooltips/ProjectTooltip";
import urlApi from "shared/actions/urlApi";
import ProfileFoldersSlice, { ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { MAX_FOLDERS_PROJECTS_COUNT } from "shared/pages/designer/components/DesignerFolders/DesignerFolders";
import { createPortal } from "react-dom";

export enum ORIENTATION_TYPE {
    project = 'project',
    card = 'card'
}

export const FoldersDropdown: FC<{ 
    projectId: string, 
    isDraft?: boolean,
    isContinuumSite?: boolean,
    isProfile?: boolean,
    orientationType?: ORIENTATION_TYPE
    toggleTooltip?: (v: any) => void 
}> = ({ 
    projectId,
    orientationType = ORIENTATION_TYPE.card,
    isContinuumSite, 
    isProfile,
    isDraft,
    toggleTooltip 
}) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const { info, error } = useToasts();
    const [open,setOpen] = useState(false);
    const isGeo = AppTester.isGeograffee();
    const [time, setTime] = useState(Date.now());
    const isProjectPage = !!useRouteMatch(LinkTo.project);
    const foldersList = useSelector(ProfileFoldersSelector)?.foldersList;
    const { onAddProject, onRemoveProject } = useFoldersController();

    const onUpdateFolderList = (data: FolderT[] | null) => {
        dispatch(ProfileFoldersSlice.actions.setFolders(data))
    }

    const listVisible = useMemo(() => {
        if (!isMobile) {
            return true;
        }
        return open
    } , [open, isMobile])

    const handleOpen = () => {
        setOpen(!open)
    }

    const initFolders = async () => {
        const data = await urlApi().fetchFolders(profile?.designerId ?? '')

        
        if (Array.isArray(data)) {
            onUpdateFolderList(data)
        };
    }

    useEffect(() => {
        if (isProjectPage && !foldersList?.length) {
            initFolders()
        }
    } , [])

    const isActive = (id: string) => {
        const item = foldersList?.find((folder) => folder?.id === id)
        return !!item?.entityIds?.includes(projectId) || false
    }

    const onUpdateData = (tm: number) => {
        setTime(tm)
    }

    const onToggleFolderClick = (id: string) => {
        const item = foldersList?.find((folder) => id === folder?.id);
        const isActiveFolder = isActive(id);

        if (!item) return null;

        if (isActiveFolder) {
            onRemoveProject(
                {...item, 
                    entityIds: [projectId]
                })?.then((r) => {
                    onUpdateData(Date.now());
                    if (!isProjectPage) {
                        initFolders()
                    }
                }
            )
            return;
        } 

        //@ts-ignore
        const allowedCount = item?.entityIds?.length < MAX_FOLDERS_PROJECTS_COUNT;

        if (!allowedCount) {
            error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
            return;
        }

        if (isDraft) {
            error(t('folders.msg.block_draft_msg'))
            return;
        }

        const hasInCurrentFolder = item?.entityIds?.includes(projectId);

        if (hasInCurrentFolder) {
            onRemoveProject(
                {...item, 
                    entityIds: [projectId]
                })?.then((r) => {
                    onUpdateData(Date.now());
                    if (!isProjectPage) {
                        initFolders()
                    }
                }
            )
            return;
        }

        const hasInAnyFolders = foldersList?.filter((folder) => folder?.entityIds?.includes(projectId))
        const noToast = !!hasInAnyFolders?.length;

        if (hasInAnyFolders) {
            Promise.all(hasInAnyFolders?.map(async(fl) => {
                onRemoveProject({...fl, entityIds: [projectId]}, noToast)?.then((r) => {
                    info(`проект перемещен из папки ${fl.name} в папку ${item?.name}` )
                })
            } ))
        }

        onAddProject(
            {...item, 
                entityIds: [projectId]
            }, noToast).then((r) => {

                if (r?.status === 400) {
                    error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
                    return;
                }

                onUpdateData(Date.now());
                if (!isProjectPage) {
                    initFolders()
                }
            }
        )

        
    }

    const divRef = useRef<HTMLDivElement>(null);

    if (!foldersList?.length) {
        return null
    }

    return (
        <StyledDropdownListItem
            isContinuumSite={isContinuumSite} 
            onMouseEnter={() => {
                toggleTooltip?.(true)
            }}
            onMouseLeave={() => {
                toggleTooltip?.(false)
            }}
            isProfile={isProfile} 
            onClick={(ev) => {
                ev.preventDefault()
                ev.stopPropagation();
                handleOpen()
            }}
        >
            <Wrapper ref={divRef}>
                <Dropdown
                    hoverOpen
                    dark={isGeo}
                    offsetArrowY={0}
                    offsetX={-20}
                    target={
                        <span
                            style={{ width: '100%' ,lineHeight: '18px', margin: '10px 0 14px 0', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-between' }}
                        >
                            {`${t('add_to_folder')}`}
                            <ShevronSvg />
                        </span>}
                    >
                        {!!listVisible && foldersList?.map(({ name, id, entityIds = [] }) => {
                            return (
                                <DropdownListItem 
                                    onClick={() => onToggleFolderClick(id ?? '')}
                                >
                                    <span>{name}</span>
                                    <ApprovedIcon fill={Colors.galBlack100} display={isActive(id ?? '')}/>
                                </DropdownListItem>
                            )
                        })}
                </Dropdown>
            </Wrapper>
        </StyledDropdownListItem>

    )
}
import { Colors, Dropdown, DropdownListItem, useIsMobile, useToasts } from "@brg/ui";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { Wrapper } from "./style";
import {ReactComponent as ShevronSvg } from "./Arrows.svg";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { AppTester } from "supporting";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import { useDispatch, useSelector } from "react-redux";
import { useProfile } from "@brg/auth";
import { useRouteMatch } from "react-router-dom";
import { LinkTo } from "shared/constants/linkTo";
import { StyledDropdownListItem } from "../tooltips/ProjectTooltip";
import urlApi from "shared/actions/urlApi";
import ProfileFoldersSlice, { ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { MAX_FOLDERS_PROJECTS_COUNT } from "shared/pages/designer/components/DesignerFolders/DesignerFolders";
import { createPortal } from "react-dom";

export enum ORIENTATION_TYPE {
    project = 'project',
    card = 'card'
}

export const FoldersDropdown: FC<{ 
    projectId: string, 
    isDraft?: boolean,
    isContinuumSite?: boolean,
    isProfile?: boolean,
    orientationType?: ORIENTATION_TYPE
    toggleTooltip?: (v: any) => void 
}> = ({ 
    projectId,
    orientationType = ORIENTATION_TYPE.card,
    isContinuumSite, 
    isProfile,
    isDraft,
    toggleTooltip 
}) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const { info, error } = useToasts();
    const [open,setOpen] = useState(false);
    const isGeo = AppTester.isGeograffee();
    const [time, setTime] = useState(Date.now());
    const isProjectPage = !!useRouteMatch(LinkTo.project);
    const foldersList = useSelector(ProfileFoldersSelector)?.foldersList;
    const { onAddProject, onRemoveProject } = useFoldersController();

    const onUpdateFolderList = (data: FolderT[] | null) => {
        dispatch(ProfileFoldersSlice.actions.setFolders(data))
    }

    const listVisible = useMemo(() => {
        if (!isMobile) {
            return true;
        }
        return open
    } , [open, isMobile])

    const handleOpen = () => {
        setOpen(!open)
    }

    const initFolders = async () => {
        const data = await urlApi().fetchFolders(profile?.designerId ?? '')

        
        if (Array.isArray(data)) {
            onUpdateFolderList(data)
        };
    }

    useEffect(() => {
        if (isProjectPage && !foldersList?.length) {
            initFolders()
        }
    } , [])

    const isActive = (id: string) => {
        const item = foldersList?.find((folder) => folder?.id === id)
        return !!item?.entityIds?.includes(projectId) || false
    }

    const onUpdateData = (tm: number) => {
        setTime(tm)
    }

    const onToggleFolderClick = (id: string) => {
        const item = foldersList?.find((folder) => id === folder?.id);
        const isActiveFolder = isActive(id);

        if (!item) return null;

        if (isActiveFolder) {
            onRemoveProject(
                {...item, 
                    entityIds: [projectId]
                })?.then((r) => {
                    onUpdateData(Date.now());
                    if (!isProjectPage) {
                        initFolders()
                    }
                }
            )
            return;
        } 

        //@ts-ignore
        const allowedCount = item?.entityIds?.length < MAX_FOLDERS_PROJECTS_COUNT;

        if (!allowedCount) {
            error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
            return;
        }

        if (isDraft) {
            error(t('folders.msg.block_draft_msg'))
            return;
        }

        const hasInCurrentFolder = item?.entityIds?.includes(projectId);

        if (hasInCurrentFolder) {
            onRemoveProject(
                {...item, 
                    entityIds: [projectId]
                })?.then((r) => {
                    onUpdateData(Date.now());
                    if (!isProjectPage) {
                        initFolders()
                    }
                }
            )
            return;
        }

        const hasInAnyFolders = foldersList?.filter((folder) => folder?.entityIds?.includes(projectId))
        const noToast = !!hasInAnyFolders?.length;

        if (hasInAnyFolders) {
            Promise.all(hasInAnyFolders?.map(async(fl) => {
                onRemoveProject({...fl, entityIds: [projectId]}, noToast)?.then((r) => {
                    info(`проект перемещен из папки ${fl.name} в папку ${item?.name}` )
                })
            } ))
        }

        onAddProject(
            {...item, 
                entityIds: [projectId]
            }, noToast).then((r) => {

                if (r?.status === 400) {
                    error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
                    return;
                }

                onUpdateData(Date.now());
                if (!isProjectPage) {
                    initFolders()
                }
            }
        )

        
    }

    const divRef = useRef<HTMLDivElement>(null);

    if (!foldersList?.length) {
        return null
    }

    return (
        <StyledDropdownListItem
            isContinuumSite={isContinuumSite} 
            onMouseEnter={() => {
                toggleTooltip?.(true)
            }}
            onMouseLeave={() => {
                toggleTooltip?.(false)
            }}
            isProfile={isProfile} 
            onClick={(ev) => {
                ev.preventDefault()
                ev.stopPropagation();
                handleOpen()
            }}
        >
            <Wrapper ref={divRef}>
                <Dropdown
                    hoverOpen
                    dark={isGeo}
                    offsetArrowY={0}
                    offsetX={-20}
                    target={
                        <span
                            style={{ width: '100%' ,lineHeight: '18px', margin: '10px 0 14px 0', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-between' }}
                        >
                            {`${t('add_to_folder')}`}
                            <ShevronSvg />
                        </span>}
                    >
                        {!!listVisible && foldersList?.map(({ name, id, entityIds = [] }) => {
                            return (
                                <DropdownListItem 
                                    onClick={() => onToggleFolderClick(id ?? '')}
                                >
                                    <span>{name}</span>
                                    <ApprovedIcon fill={Colors.galBlack100} display={isActive(id ?? '')}/>
                                </DropdownListItem>
                            )
                        })}
                </Dropdown>
            </Wrapper>
        </StyledDropdownListItem>

    )
}
